import { Button, Flex, Text, Tooltip } from '@mantine/core';
import { MetaBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/components/MetaBadge/MetaBadge.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	InvestmentDetailsAllocation,
	InvestmentWorkflowType,
} from '@dealstack/shared/lib/generated';
import { formatPaymentAmount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.utils.tsx';
import { InvestmentAllocationCardStyles } from './InvestmentAllocationCard.styles.tsx';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	getInvestmentDisplayType,
	getInvestmentDisplayTypeLabel,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/utils.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

interface Props {
	allocation: InvestmentDetailsAllocation;
	investmentType: InvestmentWorkflowType | undefined;
	canMutateAllocations: boolean;
	onEditAllocation?: (allocation: InvestmentDetailsAllocation) => void;
}

export const InvestmentAllocationCard = ({
	allocation,
	investmentType,
	canMutateAllocations,
	onEditAllocation,
}: Props) => {
	const investmentDisplayLabel = investmentType
		? getInvestmentDisplayTypeLabel(getInvestmentDisplayType(investmentType))
		: null;
	const tMipManage = useTranslationMipManage();
	const tSecurityTypeTranslation = useSecurityTypeTranslation();

	const editAllocationButton = (
		<Button
			variant="default"
			color="dark.8"
			styles={{
				root: {
					border: 0,
					padding: '0.25rem',
				},
			}}
			onClick={() => onEditAllocation?.(allocation)}
			disabled={!canMutateAllocations}
		>
			<Icon variant="settings" size="lg" />
		</Button>
	);

	return (
		<InvestmentAllocationCardStyles>
			<Flex justify="space-between" align="center">
				<Text fz={14} fw={600} c="dark.0" mb="0.25rem">
					{allocation.issuingEntityName}
				</Text>

				{canMutateAllocations ? (
					editAllocationButton
				) : (
					<Tooltip label={tMipManage('investment_allocations.frozen_tooltip')}>
						<div>{editAllocationButton}</div>
					</Tooltip>
				)}
			</Flex>

			<Text fz={18} fw={600} c="dark.8" mb="0.5rem">
				{allocation.securityName}
			</Text>

			<Flex align="center" gap="0.25rem" mb="1rem">
				<Text fw={600} c="dark.0">
					{tSecurityTypeTranslation(allocation.securityType)}
				</Text>

				{allocation.transactionType && <MetaBadge label={allocation.transactionType} />}

				{investmentDisplayLabel && <MetaBadge label={investmentDisplayLabel} />}
			</Flex>

			<Flex direction="column" gap="0.5rem">
				<Flex justify="space-between" align="center">
					<Text c="dark.0">{tMipManage('investment_allocation_card.units')}</Text>

					<Text fw={600} c="dark.8">
						{formatNumber({
							value: allocation.units,
						})}
					</Text>
				</Flex>

				<Flex justify="space-between" align="center">
					<Text c="dark.0">{tMipManage('investment_allocation_card.total_cost')}</Text>

					<Text fw={600} c="dark.8">
						{formatPaymentAmount(
							allocation.cost,
							allocation.securityCurrency,
							'symbol'
						)}
					</Text>
				</Flex>

				<Flex justify="space-between" align="center">
					<Text c="dark.0">{tMipManage('investment_allocation_card.vesting_term')}</Text>

					<Text fw={600} c="dark.8">
						{allocation.vestingTermName || LONG_DASH}
					</Text>
				</Flex>
			</Flex>
		</InvestmentAllocationCardStyles>
	);
};
